import { Link } from "react-router-dom";
import SEOMeta from "@/components/base/SEOMeta";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import HealthDisclaimer from "@/components/base/HealthDisclaimer";

export default function Support() {
  const supportSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Recovery Coaching & Support Services | Serena's Quiet Recovery Space",
    "description": "Private alcohol recovery coaching and check-in support from Serena Raimes. One-to-one video sessions, group support, and between-session messaging — flexible, compassionate, and completely confidential.",
    "url": "https://serenasquietrecoveryspace.com/support",
    "about": {
      "@type": "Service",
      "name": "Private Alcohol Recovery Coaching & Support",
      "provider": {
        "@type": "Person",
        "name": "Serena Raimes",
        "jobTitle": "Recovery Coach"
      },
      "serviceType": "Alcohol Recovery Coaching",
      "description": "One-to-one and group video sessions to explore drinking patterns, set gentle goals, and create practical, compassionate change plans. Includes text and voice message check-ins between sessions."
    }
  };

  return (
    <main className="min-h-screen pt-20 md:pt-24">
      <SEOMeta
        title="Recovery Coaching & Support | Serena's Quiet Recovery Space"
        description="Private alcohol recovery coaching from Serena Raimes. One-to-one video sessions, group support, and between-session check-ins. Flexible, compassionate, and completely confidential support."
        canonicalUrl="/support"
        ogType="website"
        schema={supportSchema}
      />
      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <Breadcrumbs
              items={[
                { label: "Home", href: "/" },
                { label: "Coaching & Support" },
              ]}
            />
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-foreground-900 mb-4 text-center">
              Support &amp; Services
            </h1>
            <p className="font-body text-sm md:text-base text-foreground-600 text-center max-w-2xl mx-auto mb-12 md:mb-16">
              Every service is designed to meet you where you are, with <strong>flexibility and compassion</strong> at the heart of everything.
            </p>

            <div className="space-y-12 md:space-y-16">
              {/* Coaching Service */}
              <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
                <div className="w-full md:w-1/2">
                  <div className="relative overflow-hidden rounded-lg">
                    <img
                      src="https://readdy.ai/api/search-image?query=Serene%20modern%20home%20office%20with%20soft%20natural%20lighting%2C%20minimalist%20desk%20setup%2C%20laptop%20open%20to%20video%20call%2C%20warm%20neutral%20tones%2C%20potted%20plant%2C%20ceramic%20cup%2C%20editorial%20interior%20photography%2C%20calm%20professional%20atmosphere&width=800&height=600&seq=coaching-session&orientation=landscape"
                      alt="Online private recovery coaching session with Serena Raimes"
                      title="Private Recovery Coaching Sessions | Serena's Quiet Recovery Space"
                      width="800"
                      height="600"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
                <div className="w-full md:w-1/2">
                  <h2 className="font-heading text-2xl md:text-3xl font-medium text-foreground-900 mb-4">
                    Coaching
                  </h2>
                  <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                    <strong>One-to-one and group video sessions</strong> to explore your drinking patterns, set gentle goals, and create practical, compassionate change plans.
                  </p>
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-primary-700 mt-0.5">
                        <i className="ri-check-line text-sm" />
                      </div>
                      <p className="font-body text-sm text-foreground-700">
                        <strong>Initial assessment</strong> to understand your unique situation
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-primary-700 mt-0.5">
                        <i className="ri-check-line text-sm" />
                      </div>
                      <p className="font-body text-sm text-foreground-700">
                        <strong>Personalised goal setting</strong> that respects your pace
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-primary-700 mt-0.5">
                        <i className="ri-check-line text-sm" />
                      </div>
                      <p className="font-body text-sm text-foreground-700">
                        Weekly or bi-weekly <strong>video sessions via secure platform</strong>
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-primary-700 mt-0.5">
                        <i className="ri-check-line text-sm" />
                      </div>
                      <p className="font-body text-sm text-foreground-700">
                        <strong>Group sessions</strong> available for peer support
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/contact"
                    className="inline-block bg-primary-600 text-background-50 font-body text-sm font-medium px-6 py-3 rounded-md hover:bg-primary-700 transition-all duration-300 whitespace-nowrap"
                  >
                    Book a Session
                  </Link>
                </div>
              </div>

              {/* Check-ins Service */}
              <div className="flex flex-col md:flex-row-reverse gap-8 md:gap-12 items-start">
                <div className="w-full md:w-1/2">
                  <div className="relative overflow-hidden rounded-lg">
                    <img
                      src="https://readdy.ai/api/search-image?query=Close-up%20of%20smartphone%20on%20wooden%20table%20showing%20warm%20text%20conversation%2C%20soft%20morning%20light%20from%20window%2C%20coffee%20cup%20nearby%2C%20minimalist%20home%20setting%2C%20editorial%20lifestyle%20photography%2C%20calm%20supportive%20atmosphere&width=800&height=600&seq=check-in-support&orientation=landscape"
                      alt="Between-session check-in support via text and voice messages"
                      title="Recovery Check-in Support | Serena's Quiet Recovery Space"
                      width="800"
                      height="600"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
                <div className="w-full md:w-1/2">
                  <h2 className="font-heading text-2xl md:text-3xl font-medium text-foreground-900 mb-4">
                    Check-ins
                  </h2>
                  <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                    Short <strong>text or voice message support</strong> between sessions, so you never feel alone navigating cravings, slips, or everyday stressors.
                  </p>
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-primary-700 mt-0.5">
                        <i className="ri-check-line text-sm" />
                      </div>
                      <p className="font-body text-sm text-foreground-700">
                        <strong>Text and voice message</strong> support between sessions
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-primary-700 mt-0.5">
                        <i className="ri-check-line text-sm" />
                      </div>
                      <p className="font-body text-sm text-foreground-700">
                        <strong>Gentle accountability</strong> without feeling policed
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-primary-700 mt-0.5">
                        <i className="ri-check-line text-sm" />
                      </div>
                      <p className="font-body text-sm text-foreground-700">
                        Response within <strong>24 hours on business days</strong>
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-primary-700 mt-0.5">
                        <i className="ri-check-line text-sm" />
                      </div>
                      <p className="font-body text-sm text-foreground-700">
                        Available as <strong>standalone or add-on</strong> to coaching
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/contact"
                    className="inline-block bg-primary-600 text-background-50 font-body text-sm font-medium px-6 py-3 rounded-md hover:bg-primary-700 transition-all duration-300 whitespace-nowrap"
                  >
                    Get Message Support
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-14">
              <HealthDisclaimer variant="box" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}